import type {
  ActiveFirm,
  AuthenticatedIdentity,
  AuthenticatedProfile,
  FirmMembership,
} from './authentication-contract.ts';

// A person may be staff in several firms. The firm they work in is a browser
// preference per user; the database still authorizes every firm they belong to.
const ACTIVE_FIRM_KEY_PREFIX = 'fl_active_firm:';

function localStorageOrNull(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage || null;
  } catch {
    return null;
  }
}

export function readStoredActiveFirmId(userId: string): string | null {
  try {
    const value = localStorageOrNull()?.getItem(`${ACTIVE_FIRM_KEY_PREFIX}${userId}`);
    return value?.trim() ? value : null;
  } catch {
    return null;
  }
}

export function storeActiveFirmId(userId: string, firmId: string): void {
  try {
    localStorageOrNull()?.setItem(`${ACTIVE_FIRM_KEY_PREFIX}${userId}`, firmId);
  } catch {
    // Without storage the choice lasts until the page reloads.
  }
}

// The active firm's name, remembered so loading screens can show it before the
// person's memberships load.
const ACTIVE_FIRM_NAME_KEY_PREFIX = 'fl_active_firm_name:';

export function readStoredActiveFirmName(userId: string): string | null {
  try {
    const value = localStorageOrNull()?.getItem(`${ACTIVE_FIRM_NAME_KEY_PREFIX}${userId}`);
    return value?.trim() ? value : null;
  } catch {
    return null;
  }
}

export function storeActiveFirmName(userId: string, firmName: string): void {
  try {
    localStorageOrNull()?.setItem(`${ACTIVE_FIRM_NAME_KEY_PREFIX}${userId}`, firmName);
  } catch {
    // Without storage loading screens show no firm name.
  }
}

// The firms a person can work in: active memberships of firms not suspended.
export function activeMemberships(memberships: readonly FirmMembership[]): FirmMembership[] {
  return memberships.filter(
    (membership) => membership.status === 'active' && !membership.firmSuspended,
  );
}

// The preferred firm when it is still an active membership, otherwise the first.
export function resolveActiveFirmId(
  memberships: readonly FirmMembership[],
  preferredFirmId: string | null | undefined,
): string | null {
  const active = activeMemberships(memberships);
  return (
    active.find((membership) => membership.firmId === preferredFirmId)?.firmId ??
    active[0]?.firmId ??
    null
  );
}

export function activeFirmFromMemberships(
  memberships: readonly FirmMembership[],
  activeFirmId: string | null | undefined,
): ActiveFirm | null {
  const membership = activeMemberships(memberships).find(
    (candidate) => candidate.firmId === activeFirmId,
  );
  return membership ? { id: membership.firmId, name: membership.firmName ?? '' } : null;
}

// Roles and status come from the active firm's membership. Without an active
// membership (or with only suspended firms) the person keeps the roles of their
// first membership and is inactive, exactly as an inactive profile was; with no
// membership at all they have no roles.
export function profileForActiveFirm(
  identity: AuthenticatedIdentity,
  memberships: readonly FirmMembership[],
  preferredFirmId: string | null | undefined,
): AuthenticatedProfile {
  const activeFirmId = resolveActiveFirmId(memberships, preferredFirmId);
  const membership =
    memberships.find(
      (candidate) => candidate.status === 'active' && candidate.firmId === activeFirmId,
    ) ?? memberships[0];
  return {
    ...identity,
    firm_id: activeFirmId,
    roles: membership ? [...membership.roles] : [],
    status: activeFirmId !== null && membership?.status === 'active' ? 'active' : 'inactive',
  };
}
